(() => {
  const AGE_STORAGE_KEY = 'terra-alma-age-confirmed-until';

  const safelyReadAgeConfirmation = () => {
    try {
      return Number.parseInt(window.localStorage.getItem(AGE_STORAGE_KEY) || '0', 10);
    } catch (_error) {
      return 0;
    }
  };

  const safelyStoreAgeConfirmation = (expiresAt) => {
    try {
      window.localStorage.setItem(AGE_STORAGE_KEY, String(expiresAt));
    } catch (_error) {
      // The gate still closes for this visit when storage is unavailable.
    }
  };

  const initializeAgeGate = () => {
    const gate = document.querySelector('[data-terra-age-gate]');
    if (!gate || gate.dataset.initialized === 'true') return;
    gate.dataset.initialized = 'true';

    if (gate.dataset.designMode === 'true' || window.Shopify?.designMode) return;
    if (safelyReadAgeConfirmation() > Date.now()) return;

    const confirmButton = gate.querySelector('[data-terra-age-confirm]');
    const focusableElements = [...gate.querySelectorAll('button, a[href]')];
    const previousOverflow = document.body.style.overflow;

    gate.hidden = false;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => confirmButton?.focus());

    const trapFocus = (event) => {
      if (event.key !== 'Tab' || focusableElements.length === 0) return;
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    gate.addEventListener('keydown', trapFocus);
    confirmButton?.addEventListener('click', () => {
      const days = Number.parseInt(gate.dataset.storageDays || '7', 10);
      safelyStoreAgeConfirmation(Date.now() + days * 24 * 60 * 60 * 1000);
      gate.hidden = true;
      document.body.style.overflow = previousOverflow;
    });
  };

  const initializeWineFinder = (scope = document) => {
    scope.querySelectorAll('[data-terra-wine-finder]').forEach((finder) => {
      if (finder.dataset.initialized === 'true') return;
      finder.dataset.initialized = 'true';

      const buttons = [...finder.querySelectorAll('[data-finder-choice]')];
      const panels = [...finder.querySelectorAll('[data-finder-panel]')];

      const selectPanel = (panelId, shouldFocus = true) => {
        buttons.forEach((button) => {
          const selected = button.getAttribute('aria-controls') === panelId;
          button.setAttribute('aria-selected', String(selected));
          button.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          panel.hidden = panel.id !== panelId;
        });
        if (shouldFocus) finder.querySelector(`#${CSS.escape(panelId)}`)?.focus({ preventScroll: true });
      };

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => selectPanel(button.getAttribute('aria-controls')));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = buttons.length - 1;
          buttons[nextIndex].focus();
          selectPanel(buttons[nextIndex].getAttribute('aria-controls'), false);
        });
      });
    });
  };

  const initializeCartAttestations = (scope = document) => {
    scope.querySelectorAll('[data-terra-cart-attestation]').forEach((attestation) => {
      if (attestation.dataset.initialized === 'true') return;
      attestation.dataset.initialized = 'true';

      const checkbox = attestation.querySelector('[data-terra-age-attestation]');
      const container = attestation.parentElement;
      const checkoutButton = container?.querySelector('[data-terra-checkout]');
      if (!checkbox || !checkoutButton) return;

      const update = () => {
        const cartIsEmpty = checkoutButton.dataset.cartEmpty === 'true';
        checkoutButton.disabled = cartIsEmpty || !checkbox.checked;
        checkoutButton.setAttribute('aria-disabled', String(checkoutButton.disabled));
      };

      checkbox.addEventListener('change', update);
      update();

      const formId = checkoutButton.getAttribute('form');
      const form = formId ? document.getElementById(formId) : checkoutButton.closest('form');
      form?.addEventListener('submit', (event) => {
        const submitter = event.submitter;
        if (submitter?.name === 'checkout' && !checkbox.checked) {
          event.preventDefault();
          checkbox.focus();
        }
      });
    });
  };

  const initializeTerraAlma = (scope = document) => {
    initializeAgeGate();
    initializeWineFinder(scope);
    initializeCartAttestations(scope);
  };

  document.addEventListener('DOMContentLoaded', () => initializeTerraAlma());
  document.addEventListener('shopify:section:load', (event) => initializeTerraAlma(event.target));

  const cartObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initializeCartAttestations(node);
      });
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    cartObserver.observe(document.body, { childList: true, subtree: true });
  });
})();
